import {
  type SentenceDrillAction,
  type SentenceDrillMode,
  type SentenceDrillState,
} from "@-/dev.improve.haniverse.lib.sentence.drill";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import { useSentenceWords } from "@-/dev.improve.haniverse.lib.sentence.words";
import { useMemo } from "react";
import { useSentenceDrillSession } from "#use-sentence-drill-session";

import { toSentenceDrillTokens } from "#to-sentence-drill-tokens";

/**
 * The input mode each sentence-production drill kind is answered in.
 *
 * `null` for every other drill kind, which is how callers ask "is this a sentence drill at all"
 * without re-listing the kinds.
 */
export function sentenceDrillModeForDrillType(
  drillTypeKind: DrillType["kind"],
): SentenceDrillMode | null {
  if (drillTypeKind === "sentence-speech") {
    return "speech";
  }
  if (drillTypeKind === "sentence-glyphs") {
    return "glyphs";
  }
  return null;
}

export type DrillPageSentenceDrill = {
  dispatch: (action: SentenceDrillAction) => void;
  /** True while the sentence is still being segmented — the strip has nothing to render yet. */
  isPending: boolean;
  state: SentenceDrillState;
};

/**
 * Own the sentence-drill state machine for the current card.
 *
 * The segmentation is the only async part: `useSentenceWords` runs jieba (loading its wasm on
 * demand) and attaches each token's dictionary reading. Everything after that is the pure reducer,
 * which is why the interesting rules are unit-tested without React at all.
 *
 * Mounted only by a WORD drill (whose sentence is always frozen and present). Returns `null` for
 * the `word-sentence` kind, which grades one typed answer instead.
 */
export function useDrillPageSentenceDrill({
  drillTypeKind,
  sentence,
}: {
  drillTypeKind: DrillType["kind"];
  sentence: string;
}): DrillPageSentenceDrill | null {
  const mode = sentenceDrillModeForDrillType(drillTypeKind);
  // Hook order must not depend on the word drill's kind, so the query always runs — for a
  // `word-sentence` card an empty sentence resolves to `[]` without touching the DB.
  const { isPending, words } = useSentenceWords(mode === null ? "" : sentence);

  const tokens = useMemo(() => (words === undefined ? [] : toSentenceDrillTokens(words)), [words]);

  const [state, dispatch] = useSentenceDrillSession({ mode: mode ?? "glyphs", sentence, tokens });

  return useMemo(
    () => (mode === null ? null : { dispatch, isPending, state }),
    [dispatch, isPending, mode, state],
  );
}
